import type { ChangeEvent, ReactNode } from "react";
import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface Props {
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    /** Wider box for pages whose placeholder lists several things to search by. */
    wide?: boolean;
  };
  filters?: ReactNode;
  right?: ReactNode;
  /** Extra classes on the row — e.g. `mb-0` when a parent already owns the spacing. */
  className?: string;
}

/**
 * Standard list-page toolbar: leading search input, filter selects in the
 * middle, optional right-side actions. Compact, sticky-friendly.
 */
export function Toolbar({ search, filters, right, className }: Props) {
  return (
    // items-end: filter fields carry a label above the input, the search box
    // does not — aligning on the bottom edge puts every control on one row so
    // the search input lines up with the filter inputs, not their labels
    // (#18581).
    //
    // mb-4: the toolbar owns the gap to whatever follows it (usually the
    // table), the same way FilterBar does and the hand-written filter rows on
    // the settings/inventory pages do. Without it a page whose root has no
    // `space-y-*` renders the filters touching the table (/invoices, /payments).
    <div className={cn("mb-4 flex flex-wrap items-end gap-2", className)}>
      {search && (
        <div className={`relative w-full min-w-0 ${search.wide ? "sm:w-96" : "sm:w-64"}`}>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={search.value}
            placeholder={search.placeholder ?? "Tìm kiếm…"}
            onChange={(e: ChangeEvent<HTMLInputElement>) => search.onChange(e.target.value)}
            autoFocus={search.autoFocus}
            aria-label="Tìm kiếm"
            className="pl-9 pr-9"
          />
          {search.value && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => search.onChange("")}
              aria-label="Xoá tìm kiếm"
              className="absolute right-1 top-1/2 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      )}
      {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}
      {right && <div className="ml-auto flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}
